import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import {
  getPlanByPriceId,
  getSubscriptionTypeByPriceId,
  getCurrentPricePerUser,
  getTrialEndDate,
  hasUnchosenTrial
} from '@/utils/payments'

export const useSeatConfirmation = (invites) => {
  const store = useStore()

  const subscriptionStatus = ref({})
  const classifying = ref(false)
  const confirmation = ref(null)
  let classifyTimer = null
  let classifyRequestId = 0
  let pendingInvite = null

  onBeforeUnmount(() => clearTimeout(classifyTimer))

  // Whether an invitee already pays for themselves can only be answered by Stripe, so classify as
  // addresses are entered rather than guessing from the plan's own member list.
  const classify = async (emails) => {
    // clearTimeout cannot cancel a request that already started, and an earlier slow response
    // clearing the flag would leave a newer address unclassified while Invite looks ready.
    const requestId = ++classifyRequestId
    const unknown = (emails || []).filter((email) => !(email in subscriptionStatus.value))

    try {
      if (unknown.length === 0) {
        return true
      }

      classifying.value = true

      const res = await store.dispatch('payments/validateUsersSubscriptions', { users: unknown.map((email) => ({ email })) })

      for (const user of res?.users || []) {
        subscriptionStatus.value[user.email] = user.status
      }

      return unknown.every((email) => email in subscriptionStatus.value)
    } finally {
      if (requestId === classifyRequestId) {
        classifying.value = false
      }
    }
  }

  watch(invites, (emails) => {
    clearTimeout(classifyTimer)

    // A lookup armed for an address since removed must not leave Invite disabled.
    if ((emails || []).every((email) => email in subscriptionStatus.value)) {
      classifyRequestId++
      classifying.value = false

      return
    }

    classifying.value = true
    classifyTimer = setTimeout(() => classify(emails), 600)
  }, { deep: true })

  const seatedEmails = computed(() => (store.state.payments.subscriptionUsers || []).map((user) => user.email))
  const seatLimit = computed(() => store.state.payments.activeSubscription?.subscriptionDetails?.quantity || 0)

  const needSeats = computed(() => (invites.value || []).filter((email) =>
    !seatedEmails.value.includes(email) && subscriptionStatus.value[email] === 'inactive'))
  // Only buying a seat is the payer's; the payer cannot even open a non-payer's own project.
  const canInvite = computed(() => store.state.payments.isSubscriptionAdmin || needSeats.value.length === 0)
  const vacantSeats = computed(() => Math.max(seatLimit.value - seatedEmails.value.length, 0))
  // Only the seats beyond the vacant ones raise the subscription quantity, and only those are charged.
  const seatsToCharge = computed(() => Math.max(needSeats.value.length - vacantSeats.value, 0))

  const seatPrice = computed(() => {
    const priceId = store.state.payments.activeSubscription?.subscriptionDetails?.price?.id
    const plan = getPlanByPriceId(priceId)

    if (!plan) {
      return ''
    }

    const subscriptionType = getSubscriptionTypeByPriceId(priceId)
    const price = getCurrentPricePerUser(store.state.payments.activeSubscription, plan, subscriptionType)

    return subscriptionType === 'yearly' ? `US$${price}/year` : `US$${price}/month`
  })

  const seatCost = computed(() => {
    const seats = needSeats.value.length
    const vacant = `This uses ${seats} of your ${vacantSeats.value} available ${vacantSeats.value === 1 ? 'seat' : 'seats'}. Nothing extra to pay.`

    // Enterprise seats are agreed up front, so exceeding the agreement is settled with the account
    // manager rather than charged now. Every other plan bills the extra seats immediately.
    if (store.state.payments.isEnterpriseUser) {
      return seatsToCharge.value > 0
        ? `This takes you to ${seatedEmails.value.length + seats} of ${seatLimit.value} seats, beyond your agreement, so your Qlerify Account Manager will contact you to adjust your plan.`
        : vacant
    }

    if (seatsToCharge.value === 0) {
      return vacant
    }

    const activeSubscription = store.state.payments.activeSubscription

    if (hasUnchosenTrial(activeSubscription)) {
      return 'Nothing is charged during the trial.'
    }

    const rate = seatPrice.value ? ` at ${seatPrice.value}${seatsToCharge.value === 1 ? '' : ' each'}` : ''
    const trialEndDate = getTrialEndDate(activeSubscription)

    if (trialEndDate) {
      return `This adds ${seatsToCharge.value} ${seatsToCharge.value === 1 ? 'seat' : 'seats'}${rate} from ${trialEndDate}, when your plan starts. Nothing is charged today.`
    }

    return `This adds ${seatsToCharge.value} ${seatsToCharge.value === 1 ? 'seat' : 'seats'}${rate}. You'll be charged today for the rest of this billing period.`
  })

  const requestInvite = async (proceed) => {
    clearTimeout(classifyTimer)

    // A failed lookup returns no verdicts, which reads exactly like "nobody needs a seat".
    if (!await classify(invites.value)) {
      store.commit('snackbar/showMessage', {
        content: 'Could not check who needs a seat, so no invitations were sent. Please try again.',
        timeout: 8000,
        color: 'red'
      }, { root: true })

      return
    }

    if (needSeats.value.length === 0) {
      return proceed()
    }

    const seats = needSeats.value.length
    const who = seats === 1 ? needSeats.value[0] : `${seats} people`
    const target = hasUnchosenTrial(store.state.payments.activeSubscription) ? 'your free trial' : 'your plan'

    pendingInvite = proceed
    confirmation.value = {
      title: seats === 1 ? 'Add a seat?' : `Add ${seats} seats?`,
      message: `${who} will be added to ${target}. ${seatCost.value}`,
      confirmLabel: seats === 1 ? 'Add seat & invite' : 'Add seats & invite'
    }
  }

  const confirm = async () => {
    confirmation.value = null
    await pendingInvite?.()
    pendingInvite = null
  }

  const cancel = () => {
    confirmation.value = null
    pendingInvite = null
  }

  return { classifying, needSeats, canInvite, confirmation, requestInvite, confirm, cancel }
}
